// My Overview — personal performance dashboard with score + stars +
// streaks + badges, plus the regular Detailed Overview charts scoped
// to the current user. Lives at /overview/my and is gated by the
// `overview:read_my` permission.

import { useMemo } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress,
  Stack, Tooltip, Typography, alpha,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useQuery } from '@tanstack/react-query'
import type { MyPerformanceData } from '@cmms/api-client'
import { api } from '../../lib/api'
import { DetailedOverview } from './DetailedOverview'
import { useAuthStore } from '../../store/authStore'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

// ─── Top-level component ────────────────────────────────────────────

export function MyOverview() {
  const me = useAuthStore((s) => s.user)
  const perfQ = useQuery({
    queryKey: ['my-performance'],
    queryFn: () => api.analytics.myPerformance(),
    staleTime: 30_000,
  })
  const data: MyPerformanceData | undefined = perfQ.data

  return (
    <Stack spacing={3}>
      {perfQ.isError && (
        <Alert
          severity="error"
          action={
            <Button size="small" color="inherit"
              startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => perfQ.refetch()}>
              Retry
            </Button>
          }
          sx={{ fontSize: 13 }}
        >
          Couldn't load your performance data. Try again in a moment.
        </Alert>
      )}
      {perfQ.isLoading && !perfQ.data && (
        <Stack direction="row" alignItems="center" spacing={1.5}
          sx={{ color: 'text.secondary' }}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: 12.5 }}>Loading your performance…</Typography>
        </Stack>
      )}

      {data && (
        <>
          <HeroPanel data={data} name={me?.name?.split(' ')[0] ?? 'there'} />
          <KpiStrip data={data} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <StreakPanel data={data} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <BadgesPanel data={data} />
            </Grid>
          </Grid>

          {/* Org-wide Detailed Overview charts re-used but scoped to
              me on the server. We hide the People leaderboard because
              there's only one person in the data (me). */}
          {me?.id && (
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: 'primary.main', mb: 0.5 }}>
                // Activity breakdown
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5,
                letterSpacing: '-0.01em' }}>
                Where my work landed
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mb: 2 }}>
                The Detailed Overview charts, scoped to just your assignments
                and submissions.
              </Typography>
              <DetailedOverview userId={me.id} hidePeoplePanel />
            </Box>
          )}
        </>
      )}
    </Stack>
  )
}

// ─── Hero — score, stars, level, rank, sparkline ────────────────────

function HeroPanel({ data, name }: { data: MyPerformanceData; name: string }) {
  const scoreColor = data.score >= 75 ? '#10B981'
    : data.score >= 50 ? '#F59E0B' : '#EF4444'

  return (
    <Box sx={{
      p: { xs: 3, md: 4 },
      borderRadius: 3,
      position: 'relative',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      // Subtle accent glow behind the score so the hero feels alive.
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -120, right: -120,
        width: 360, height: 360,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(scoreColor, 0.18)} 0%, transparent 70%)`,
        pointerEvents: 'none',
      },
    }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ position: 'relative' }}>
        {/* Score + stars */}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: 'primary.main', mb: 0.5 }}>
            // Your performance
          </Typography>
          <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 600,
            color: 'text.secondary', mb: 2 }}>
            Hey {name}, here's how you're doing.
          </Typography>

          <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }}>
            <Typography sx={{
              fontSize: { xs: 56, md: 80 },
              fontWeight: 800,
              fontFamily: 'ui-monospace, monospace',
              color: scoreColor,
              lineHeight: 1,
              letterSpacing: '-0.04em',
            }}>
              {data.score}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.disabled' }}>
              / 100
            </Typography>
          </Stack>

          <StarsRow stars={data.stars} />

          {/* Level + progress to next */}
          <Box sx={{ mt: 2.5, maxWidth: 360 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary' }}>
                Level: <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
                  {data.level.current}
                </Box>
              </Typography>
              {data.level.next && (
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontWeight: 600 }}>
                  Next: {data.level.next}
                </Typography>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={data.level.progress}
              sx={{
                height: 8, borderRadius: 99,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 99,
                  bgcolor: scoreColor,
                },
              }}
            />
          </Box>
        </Box>

        {/* Rank + sparkline */}
        <Stack spacing={2.5} sx={{ minWidth: { md: 240 } }}>
          {data.rank.total > 0 && (
            <Box sx={{
              p: 2, borderRadius: 2,
              bgcolor: 'background.default',
              border: '1px solid', borderColor: 'divider',
            }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <EmojiEventsOutlinedIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                  textTransform: 'uppercase', color: 'text.disabled' }}>
                  Team rank
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                <Typography sx={{ fontSize: 28, fontWeight: 800,
                  fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.02em' }}>
                  #{data.rank.position}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
                  / {data.rank.total}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
                Top {100 - data.rank.percentile + 1}% in your org
              </Typography>
            </Box>
          )}

          {/* 14-day sparkline */}
          <Box sx={{
            p: 2, borderRadius: 2,
            bgcolor: 'background.default',
            border: '1px solid', borderColor: 'divider',
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <TrendingUpRoundedIcon sx={{ fontSize: 16, color: scoreColor }} />
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                textTransform: 'uppercase', color: 'text.disabled' }}>
                Last 14 days
              </Typography>
            </Stack>
            <Sparkline data={data.trend} color={scoreColor} />
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
              {data.trend.reduce((a, b) => a + b.completed, 0)} closures
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}

function StarsRow({ stars }: { stars: number }) {
  // 5-star row coloured by current count. Filled stars use the
  // accent gold; empty stars are muted so the contrast is clear.
  return (
    <Stack direction="row" spacing={0.25}>
      {[1, 2, 3, 4, 5].map((n) => (
        n <= stars ? (
          <StarRoundedIcon key={n} sx={{ fontSize: 28, color: '#F59E0B' }} />
        ) : (
          <StarOutlineRoundedIcon key={n} sx={{ fontSize: 28, color: 'text.disabled' }} />
        )
      ))}
    </Stack>
  )
}

// ─── Pure-SVG sparkline so we don't pull a chart lib for one mini ───

function Sparkline({ data, color }: {
  data: MyPerformanceData['trend']
  color: string
}) {
  const w = 200, h = 36
  if (data.length === 0) {
    return (
      <Box sx={{ width: w, height: h, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: 'text.disabled', fontSize: 11 }}>
        No data
      </Box>
    )
  }
  const max = Math.max(1, ...data.map((d) => d.completed))
  const pts = data.map((d, i) => {
    const x = (i / Math.max(1, data.length - 1)) * w
    const y = h - (d.completed / max) * h
    return [x, y] as const
  })
  const linePath = pts.map(([x, y], i) =>
    `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L ${w} ${h} L 0 ${h} Z`
  const gid = `spark-${color.replace(/[^a-z0-9]/gi, '')}`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={color} stopOpacity="0.24" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gid})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.8"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

// ─── KPI strip ──────────────────────────────────────────────────────

function KpiStrip({ data }: { data: MyPerformanceData }) {
  const tiles: Array<{
    label: string
    value: string
    target: number | null
    actual: number
    color: string
    icon: React.ReactNode
    sub: string
  }> = [
    {
      label: 'Compliance rate',
      value: `${data.kpis.complianceRate.toFixed(1)}%`,
      target: data.targets.complianceRate,
      actual: data.kpis.complianceRate,
      color: '#10B981',
      icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />,
      sub: `${data.kpis.closed} of ${data.kpis.assigned} closed`,
    },
    {
      label: 'On-time rate',
      value: `${data.kpis.onTimeRate.toFixed(1)}%`,
      target: data.targets.onTimeRate,
      actual: data.kpis.onTimeRate,
      color: '#3B82F6',
      icon: <BoltOutlinedIcon sx={{ fontSize: 18 }} />,
      sub: `of closures hit due date`,
    },
    {
      label: 'Checklist quality',
      value: `${data.kpis.checklistRate.toFixed(1)}%`,
      target: data.targets.checklistRate,
      actual: data.kpis.checklistRate,
      color: '#7C3AED',
      icon: <ChecklistRtlOutlinedIcon sx={{ fontSize: 18 }} />,
      sub: `${data.kpis.checklistsSubmitted} of ${data.kpis.checklistsTotal} submitted`,
    },
    {
      label: 'Avg response',
      value: data.kpis.avgResponseHours > 0
        ? `${data.kpis.avgResponseHours.toFixed(1)}h`
        : '—',
      target: null,
      actual: 0,
      color: '#F59E0B',
      icon: <LocalFireDepartmentOutlinedIcon sx={{ fontSize: 18 }} />,
      sub: 'from create to close',
    },
  ]

  return (
    <Grid container spacing={2}>
      {tiles.map((t, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{
            p: 2, borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
            position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              bgcolor: t.color,
            },
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
                textTransform: 'uppercase', color: 'text.disabled' }}>
                {t.label}
              </Typography>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1,
                bgcolor: alpha(t.color, 0.12), color: t.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{t.icon}</Box>
            </Stack>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: t.color,
              fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.02em',
              lineHeight: 1 }}>
              {t.value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
              {t.sub}
            </Typography>
            {t.target !== null && (
              <Box sx={{ mt: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    color: 'text.disabled' }}>
                    vs target {t.target}%
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 700,
                    color: t.actual >= t.target ? '#10B981' : '#EF4444' }}>
                    {t.actual >= t.target ? 'On track' : `${(t.target - t.actual).toFixed(1)} pp behind`}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (t.actual / t.target) * 100)}
                  sx={{
                    height: 4, borderRadius: 99,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 99,
                      bgcolor: t.actual >= t.target ? '#10B981' : t.color,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

// ─── Streak panel ───────────────────────────────────────────────────

function StreakPanel({ data }: { data: MyPerformanceData }) {
  const streak = data.streak.days
  const milestone = data.streak.next_milestone
  const progress = data.streak.target_progress
  const onFire = streak >= 7

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <LocalFireDepartmentOutlinedIcon
          sx={{ fontSize: 18, color: onFire ? '#EF4444' : 'text.disabled' }} />
        <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: 'text.secondary' }}>
          Activity streak
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
        <Typography sx={{
          fontSize: 56, fontWeight: 800,
          fontFamily: 'ui-monospace, monospace',
          letterSpacing: '-0.04em',
          color: onFire ? '#EF4444' : 'text.primary',
          lineHeight: 1,
        }}>
          {streak}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>
          {streak === 1 ? 'day' : 'days'}
        </Typography>
        {onFire && (
          <Typography sx={{ fontSize: 13, fontWeight: 800,
            color: '#EF4444', ml: 1 }}>
            🔥 on fire
          </Typography>
        )}
      </Stack>

      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
        {streak === 0
          ? 'Close any work order today to start a streak.'
          : `Keep going — you're ${milestone - streak} day${milestone - streak === 1 ? '' : 's'} from a ${milestone}-day milestone.`}
      </Typography>

      <Box sx={{ mb: 0.5 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5,
            color: 'text.disabled' }}>
            Progress to {milestone} days
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700,
            color: 'text.primary' }}>
            {progress}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8, borderRadius: 99,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 99,
              bgcolor: onFire ? '#EF4444' : '#3B82F6',
            },
          }}
        />
      </Box>
    </Box>
  )
}

// ─── Badges grid ────────────────────────────────────────────────────

function BadgesPanel({ data }: { data: MyPerformanceData }) {
  // Earned badges first, then unearned for "what's next" motivation.
  const sorted = useMemo(() =>
    [...data.badges].sort((a, b) => (a.earned === b.earned ? 0 : a.earned ? -1 : 1)),
    [data.badges])
  const earnedCount = data.badges.filter((b) => b.earned).length
  const totalCount = data.badges.length

  const tierColor = (tier: string) =>
    tier === 'gold'   ? '#F59E0B'
    : tier === 'silver' ? '#94A3B8'
    : '#A16207' // bronze

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary' }}>
            Achievements
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.25 }}>
            {earnedCount} of {totalCount} unlocked
          </Typography>
        </Box>
        <Chip
          icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 14 }} />}
          label={`${earnedCount}/${totalCount}`}
          size="small"
          sx={{ fontWeight: 700, fontSize: 11 }}
        />
      </Stack>

      <Grid container spacing={1.5}>
        {sorted.map((b) => {
          const c = tierColor(b.tier)
          return (
            <Grid key={b.id} size={{ xs: 6, sm: 4 }}>
              <Tooltip
                title={
                  <Box>
                    <Box sx={{ fontWeight: 800, fontSize: 12.5 }}>{b.label}</Box>
                    <Box sx={{ fontSize: 11.5, mt: 0.25 }}>{b.hint}</Box>
                    <Box sx={{ fontSize: 10.5, mt: 0.4, opacity: 0.8,
                      textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {b.tier} {b.earned ? '· earned' : '· locked'}
                    </Box>
                  </Box>
                }
                arrow placement="top"
              >
                <Box sx={{
                  p: 1.5, borderRadius: 1.5, textAlign: 'center',
                  bgcolor: b.earned ? alpha(c, 0.1) : 'action.hover',
                  border: '1px solid',
                  borderColor: b.earned ? alpha(c, 0.3) : 'divider',
                  cursor: 'help',
                  position: 'relative',
                  transition: 'transform 120ms, box-shadow 120ms',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: b.earned
                      ? `0 6px 18px -6px ${alpha(c, 0.5)}`
                      : 'none',
                  },
                  filter: b.earned ? 'none' : 'grayscale(0.8)',
                  opacity: b.earned ? 1 : 0.5,
                }}>
                  <Typography sx={{ fontSize: 28, lineHeight: 1, mb: 0.5 }}>
                    {b.icon}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 800,
                    color: b.earned ? c : 'text.disabled',
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {b.label}
                  </Typography>
                  {!b.earned && (
                    <LockOutlinedIcon sx={{
                      position: 'absolute', top: 6, right: 6,
                      fontSize: 12, color: 'text.disabled',
                    }} />
                  )}
                </Box>
              </Tooltip>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}
